import {ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, Injectable, input, output, signal, viewChild} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AgendaelementId, GruppeId, GruppeMedlemListeModell, OpprettSakerSpesifikasjon, SakstypeModell, SakstypeRessursClient, TemaModell, TemaRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {combineLatest, Observable} from "rxjs";
import {MatButtonModule} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {TranslateModule} from "@ngx-translate/core";
import {scrollTilBunn} from "web-ng2/delt/dom/scrollIntoView";
import {NySakSkjema, NySakSkjemaTilleggsdata, NySakSkjemaVerdier, SakNyKontroller} from "../sak-ny/sak-ny";
import {erTomListe, siste} from "felles-ng2/støttefunksjoner/lister";
import {erStreng, erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {filter, map} from "rxjs/operators";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {InlineSkjemaBlokkKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-blokk/inline-skjema-blokk";
import {MatCheckbox} from "@angular/material/checkbox";
import {Gruppeoppsett} from "felles-ng2/tjenester/Gruppeoppsett";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MatIcon} from "@angular/material/icon";
import {MøteModellDLM} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";

const debug = initDebugger('NyeSakerSkjemaKontroller');

export type NyeSakerSkjema = NyeSakerSkjemaKontroller['skjema'];
export type NyeSakerSkjemaVerdier = NyeSakerSkjema['value'];


@Component({
    selector: 'nye-saker-skjema',
    templateUrl: './nye-saker-skjema.html',
    styleUrls: ['./nye-saker-skjema.scss'],
    host: {
        '[class.viser-fullt-skjema]': 'viserFulltSkjema()'
    },
    imports: [
        NavigasjonforhindretFlashDirektiv,
        TranslateModule,
        MatButtonModule,
        VisSpinnerDirektiv,
        SakNyKontroller,
        FormsModule,
        MatSlideToggleModule,
        MatTooltip,
        ReactiveFormsModule,
        TipsKomponent,
        InlineSkjemaKomponent,
        InlineSkjemaHandlingerKomponent,
        InlineSkjemaBlokkKomponent,
        MatCheckbox,
        MatIcon
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NyeSakerSkjemaKontroller {
    static ajsSelector = 'nyeSakerSkjema'; //@todo fjernes

    private skjemaelement = viewChild.required<ElementRef<HTMLFormElement>>('skjemaelement');

    vedVisFulltSkjemaAktivert = output<boolean>();
    /** Kalles når nye saker har blitt opprettet, med liste med id for nye saker som argument */
    vedNyeSakerOpprettet = output<AgendaelementId[]>();

    moete = input.required<MøteModellDLM>();
    /** Dersom sann vises tips om hvordan man legger til nye saker (forutsatt at ikke fullt skjema vises og skjema ikke er deaktivert) */
    visNySakTips = input(false);

    skjema = new FormGroup({
        saker: new FormArray<NySakSkjema>([]),
        sorterEtterSakstype: new FormControl(false), //populeres av initSakerSkjema()
        huskSorterEtterSakstypeValg: new FormControl(false),
        informerSaksansvarlig: new FormControl(true)
    });
    tilleggsdata: NySakSkjemaTilleggsdata = {temaer: [], gruppemedlemmer: [], sakstyper: []};
    sorterEtterSakstypeTips = ''; //blir init når tilleggsdata.sakstyper er lastet
    viserFulltSkjema = signal(false);
    viserDetaljer = signal(false);
    /** Dersom menyer er deaktivert antar vi at et annet skjema er aktivt og vi forhindrer da aktivering av redigeringsmodus */
    skjemaDeaktivert = computed(() => !this.viserFulltSkjema() && this.navigasjonDeaktivering.navigasjonDeaktivertSomSignal());
    viserNySakTips = computed(() => this.visNySakTips() && !this.viserFulltSkjema() && !this.skjemaDeaktivert());
    lagrer = asynkArbeidPågårSignal();

    initiellSorterEtterSakstypeEndret = signal(false); //fungerer også når ikke signal.. men vi bruker signal på øvrige

    private initiellSorterEtterSakstype = this.skjema.value.sorterEtterSakstype;

    constructor(private avhengigheter: NyeSakerSkjemaKontrollerAvhengigheter,
                private oversettelser: Oversettelser,
                private gjeldendeTidssone: GjeldendeTidssone,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private gruppeoppsett: Gruppeoppsett,
                private destroyRef: DestroyRef) {
        this.deaktiverSkjemaVedNavigasjonDeaktivert();
        this.initSkjema();
        //sørg for å legge til nye rader når tittel-felt fylles ut
        this.skjema.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(skjemaverdier => this.vedEndringer(skjemaverdier));
    }

    forrigeSakDag(indeks: number) {
        return indeks == 0 ? 0 : this.skjema.controls.saker.value[indeks - 1].dag ?? 0;
    }

    slettSak(indeks: number) {
        this.skjema.controls.saker.removeAt(indeks);
    }

    antallSaker() {
        return this.ikkeTommeRader().length
    }

    giFokusTilNySakFelt() {
        const tittelFelt: HTMLInputElement = this.skjemaelement().nativeElement.querySelector('.nysak-kontainer:last-of-type input[name="tittel"]')!;
        tittelFelt.focus();
    }

    async lagre() {
        await this.lagrer.utfør(async () => {
            const spesifikasjon = this.somSpesifikasjon();
            await this.avhengigheter.opprett(spesifikasjon);
            if (spesifikasjon.huskSorterEtterSakstypeValg)
                this.gruppeoppsett.oppdaterSorterSakerEtterSakstype(this.gruppeId(), spesifikasjon.sorterEtterSakstype);

            this.resetSkjema(); //fører til at deaktiverVisFulltSkjema() blir kalt

            this.vedNyeSakerOpprettet.emit(spesifikasjon.saker.map(sak => sak.id));
        });
    }

    avbryt() {
        this.resetSkjema();
    }

    // private hjelpemetoder

    private initSkjema() {
        //sørger for at det finnes en tom linje til slutt for reg av ny sak
        this.skjema.controls.saker.clear();
        this.skjema.controls.saker.push(SakNyKontroller.opprettSkjema())

    }

    /** Resetter skjema ved å fjerne alle eksisterende rader for så å legge til en initiell rad.
     * Fører til at {@link #deaktiverVisFulltSkjema} kalles via {@link vedEndringer},
     * dermed blir {@link #vedLukk} kalt */
    private resetSkjema() {
        this.initSkjema();
    }

    private gruppeId(): GruppeId {
        return this.moete().eiendeGruppeId;
    }

    private vedEndringer(skjemaverdier: NyeSakerSkjemaVerdier) {
        debug('vedEndringer', skjemaverdier)
        //sørger for at det alltid vises en tom sak til slutt i listen
        if (erIkkeTomRad(siste(skjemaverdier.saker!))) this.leggTilNyRadOgScrollTil()

        //sørger for at det ikke vises mer enn en tom sak i slutten av listen
        if (skjemaverdier.saker!.slice(-2).filter(rad => erTomRad(rad)).length > 1) //sann dersom de to siste radene er tomme
          // fjerner nest siste rad (ikke siste - denne fungere som "trigger" for nye rader, dessuten måtte vi har fjernet required-validering som ligger nest siste om vi fjerner siste...)
            this.skjema.controls.saker.removeAt(this.skjema.controls.saker.length - 2);

        //har valgt å ikke fjerne tomme rader etterfulgt av ikke-tomme rader - disse må bruker selv slette

        if (this.skjema.controls.saker.length > 1) this.aktiverVisFulltSkjema();
        else this.deaktiverVisFulltSkjema();

        //dersom sorterEtterSakstype er endret viser vi "husk" checkbox
        this.initiellSorterEtterSakstypeEndret.set(this.initiellSorterEtterSakstype !== skjemaverdier.sorterEtterSakstype);
        debug('vedEndringer post', this.skjema.controls.saker.length)
    }

    private leggTilNyRadOgScrollTil() {
        this.skjema.controls.saker.controls.forEach(rad => rad.controls.tittel.addValidators(Validators.required));
        this.skjema.controls.saker.push(SakNyKontroller.opprettSkjema());
        //scroll til skjemaet - med forsinkelse i håp om at skjemaet da har rukket å bli tegnet ut
        scrollTilBunn(this.skjemaelement().nativeElement, 200);
    }

    private aktiverVisFulltSkjema() {
        if (this.viserFulltSkjema()) return;
        debug('aktiverVisFulltSkjema')
        this.initiellSorterEtterSakstype = this.gruppeoppsett.oppsettForGruppe(this.gruppeId()).sorterSakerEtterSakstype;
        this.skjema.controls.sorterEtterSakstype.setValue(this.initiellSorterEtterSakstype, {emitEvent: false}); //emitEvent:false for å unngå rekursjon aktiverVisFulltSkjema -> vedEndringer -> aktiverVisFulltSkjema
        this.skjema.controls.huskSorterEtterSakstypeValg.setValue(false, {emitEvent: false}); //emitEvent:false for å unngå rekursjon aktiverVisFulltSkjema -> vedEndringer -> aktiverVisFulltSkjema
        if (!this.moete().tilgang.erMoetePublisert)
            this.skjema.controls.informerSaksansvarlig.disable({emitEvent: false});
        this.viserFulltSkjema.set(true);
        if (!this.moete()!.tilgang.erMoetePublisert)
            this.skjema.controls.informerSaksansvarlig.disable({emitEvent: false});
        this.lastTilleggsdata();
        this.navigasjonDeaktivering.deaktiverNavigasjon();
        this.vedVisFulltSkjemaAktivert.emit(true);
        //$('.basisside-innhold')[0].scrollTop = 10000
    }

    private deaktiverVisFulltSkjema() {
        if (!this.viserFulltSkjema()) return;
        debug('deaktiverVisFulltSkjema')
        this.viserFulltSkjema.set(false);
        this.navigasjonDeaktivering.reaktiverNavigasjon();
        this.vedVisFulltSkjemaAktivert.emit(false);
    }

    private lastTilleggsdata() {
        debug('lastTilleggsdata')
        combineLatest([
            this.avhengigheter.gruppemedlemmer(this.moete().eiendeGruppeId),
            this.avhengigheter.sakstyper(this.moete().eiendeGruppeId),
            this.avhengigheter.temaer(this.moete().eiendeGruppeId)
        ])
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            map(([gruppemedlemmer, sakstyper, temaer]) => ({gruppemedlemmer, sakstyper, temaer})))
          .subscribe(tilleggsdata => this.setTilleggsdata(tilleggsdata));
    }

    private setTilleggsdata(tilleggsdata: NySakSkjemaTilleggsdata) {
        this.tilleggsdata = tilleggsdata;
        this.sorterEtterSakstypeTips = this.oversettelser.oversett(i18n('Sorter alle saker etter sakstype ved lagring ({sakstyper}). Se gruppens oppsett for å endre standard rekkefølge.'), {sakstyper: this.tilleggsdata.sakstyper.map(s => s.navn).join(', ')});
    }

    private ikkeTommeRader() {
        return this.skjema.value.saker!.filter(rad => erIkkeTomRad(rad));
    }

    /** sørger for at skjema deaktiveres i takt med global navigasjonDeaktivering */
    private deaktiverSkjemaVedNavigasjonDeaktivert() {
        this.navigasjonDeaktivering.navigasjonDeaktivert
          .pipe(takeUntilDestroyed(), filter(() => !this.viserFulltSkjema()))
          .subscribe(deaktivert => {
              debug('navigasjonDeaktivert: %s', deaktivert)
              //emitEvent:false for at ikke deaktivering skal trigge vedEndringer
              deaktivert ? this.skjema.disable({emitEvent: false}) : this.skjema.enable();
          });
    }

    private somSpesifikasjon(): OpprettSakerSpesifikasjon {
        return {
            saker: this.ikkeTommeRader()
              .map(rad => SakNyKontroller.somOpprettSakSpesifikasjon(rad, this.moete(), this.gjeldendeTidssone.brukersTidssone())),
            informerSaksansvarlig: this.skjema.value.informerSaksansvarlig!,
            sorterEtterSakstype: this.skjema.value.sorterEtterSakstype!,
            huskSorterEtterSakstypeValg: this.skjema.value.huskSorterEtterSakstypeValg!
        }
    }
}

@Injectable()
export class NyeSakerSkjemaKontrollerAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste,
                private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste,
                private sakstypeRessurs: SakstypeRessursClient,
                private temaRessurs: TemaRessursClient) {
    }

    gruppemedlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilgjengelige(gruppeId);
    }

    sakstyper(gruppeId: GruppeId): Observable<SakstypeModell[]> {
        return this.sakstypeRessurs.finnAlle(gruppeId).somObservable();
    }

    temaer(gruppeId: GruppeId): Observable<TemaModell[]> {
        return this.temaRessurs.finnAlle(gruppeId).somObservable();
    }

    async opprett(spesifikasjon: OpprettSakerSpesifikasjon): Promise<void> {
        await this.sakDatatjeneste.opprettSaker(spesifikasjon);
    }
}

function erIkkeTomRad(rad: NySakSkjemaVerdier | null) {
    return !erTomRad(rad);
}

function erTomRad(rad: NySakSkjemaVerdier | null) {
    if (erNil(rad?.tittel)) return true;
    //mat-autocomplete setter ved valg av malsak fra liste rad.tittel til valgt malsak-objekt,
    //så vil mat-autocomplete.optionSelected sørge for å sette rad.tittel til malsak.tittel
    //dette medfører at erTomRad() kan bli kalt med en rad der rad.tittel er et objekt og ikke en streng
    if (!erStreng(rad!.tittel)) {
        return false;
    }
    return erTomEllerNil(rad!.tittel) && erTomEllerNil(rad!.beskrivelse);
}
